import { createEnv } from "@t3-oss/env-nextjs";
import { z } from "zod/v4";

const isVercelProduction = process.env.VERCEL_ENV === "production";
const DEFAULT_DEPLOYMENT_URL = "https://www.resimoprobleme.com";

/** @param {string | undefined} value */
function isUsableDeploymentUrl(value) {
  if (!value || value.includes("${")) {
    return false;
  }

  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

function resolveDeploymentUrl() {
  if (isUsableDeploymentUrl(process.env.NEXT_PUBLIC_DEPLOYMENT_URL)) {
    return process.env.NEXT_PUBLIC_DEPLOYMENT_URL;
  }

  if (process.env.VERCEL_URL) {
    return `https://${process.env.VERCEL_URL}`;
  }

  return DEFAULT_DEPLOYMENT_URL;
}

export const env = createEnv({
  /**
   * Specify your server-side environment variables schema here. This way you can ensure the app
   * isn't built with invalid env vars.
   */
  server: {
    DATABASE_URL: z.string().url(),
    DATABASE_URL_UNPOOLED: z.string().url().optional(),
    NODE_ENV: z
      .enum(["development", "test", "production"])
      .default("development"),
    LOOPS_API_KEY: z.string(),
    LOOPS_NEWSLETTER_LIST_NAME: z.string().optional(),
    LOOPS_WEBHOOK_SIGNING_SECRET: z.string().optional(),
    META_APP_SECRET: z.string().optional(),
    NEXTAUTH_SECRET:
      process.env.NODE_ENV === "production"
        ? z.string()
        : z.string().optional(),
    NEXTAUTH_URL: z.string(),
    GOOGLE_CLIENT_ID: z.string(),
    GOOGLE_CLIENT_SECRET: z.string(),
    CASCADE_DEMO: z.string().optional(),
    SLACK_SIGNING_SECRET: z.string(),
    SLACK_BOT_TOKEN: z.string(),

    DAILY_DOMAIN: z.string().optional(),
    DAILY_API_KEY: z.string().optional(),
    DAILY_WEBHOOK_SECRET: z.string().optional(),
    DAILY_API_TIMEOUT_MS: z.coerce.number().int().positive().optional(),
    DAILY_PRESENCE_ATTEMPT_TIMEOUT_MS: z.coerce
      .number()
      .int()
      .positive()
      .optional(),

    POSTHOG_API_KEY: z.string().optional(),

    STRIPE_SECRET_KEY: z.string(),
    STRIPE_WEBHOOK_SECRET: z.string(),
    CHAT_ENCRYPTION_KEY: z.string(),
    PAYPAL_API_SECRET: z.string(),
    OPENAI_API_KEY: z.string(),
    AI_GATEWAY_API_KEY: z.string().optional(),
    FB_CONVERSATION_API_KEY: z.string(),
    BLOB_STORE_ID: z.string().min(1),
    BLOB_WEBHOOK_PUBLIC_KEY: z.string().min(1),

    VERCEL_ENV: z
      .string()
      .optional()
      .describe("Vercel environment, visable only in production"),

    WHATSAPP_BUSINESS_API_TOKEN: z.string(),

    FLAGS: isVercelProduction ? z.string() : z.string().optional(),
    FLAGS_SECRET: z.string(),
    PIXABAY_API_KEY: z.string().optional(),
    DATAFORSEO_PASSWORD: z.string().optional(),

    GOOGLE_SERVICE_ACCOUNT_KEY_BASE64: z.string(),
    GA4_API_SECRET: z.string().optional(),
    CRON_SECRET: z.string().optional(),

    GOOGLE_ADS_ANALYTICS_SERVICE_ACCOUNT_KEY_BASE64: z.string().optional(),
    GOOGLE_ADS_DEVELOPER_TOKEN: z.string().optional(),
  },

  /**
   * Specify your client-side environment variables schema here. This way you can ensure the app
   * isn't built with invalid env vars. To expose them to the client, prefix them with
   * `NEXT_PUBLIC_`.
   */
  client: {
    NEXT_PUBLIC_POSTHOG_API_KEY:
      process.env.NODE_ENV === "production"
        ? z.string()
        : z.string().optional(),
    NEXT_PUBLIC_POSTHOG_HOST:
      process.env.NODE_ENV === "production"
        ? z.string()
        : z.string().optional(),
    NEXT_PUBLIC_DEPLOYMENT_URL: z.url(),
    NEXT_PUBLIC_VERCEL_ENV: z.string().optional(),
    NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY: z.string(),
    NEXT_PUBLIC_PAYPAL_API_KEY: z.string(),
  },

  /**
   * You can't destruct `process.env` as a regular object in the Next.js edge runtimes (e.g.
   * middlewares) or client-side so we need to destruct manually.
   */
  runtimeEnv: {
    DATABASE_URL: process.env.DATABASE_URL,
    DATABASE_URL_UNPOOLED:
      process.env.DATABASE_URL_UNPOOLED ?? process.env.DATABASE_URL,
    NODE_ENV: process.env.NODE_ENV,
    NEXTAUTH_SECRET: process.env.NEXTAUTH_SECRET,
    NEXTAUTH_URL: process.env.NEXTAUTH_URL,
    GOOGLE_CLIENT_ID: process.env.GOOGLE_CLIENT_ID,
    GOOGLE_CLIENT_SECRET: process.env.GOOGLE_CLIENT_SECRET,
    NEXT_PUBLIC_POSTHOG_API_KEY: process.env.NEXT_PUBLIC_POSTHOG_API_KEY,
    NEXT_PUBLIC_POSTHOG_HOST: process.env.NEXT_PUBLIC_POSTHOG_HOST,
    LOOPS_API_KEY: process.env.LOOPS_API_KEY,
    LOOPS_NEWSLETTER_LIST_NAME: process.env.LOOPS_NEWSLETTER_LIST_NAME,
    LOOPS_WEBHOOK_SIGNING_SECRET: process.env.LOOPS_WEBHOOK_SIGNING_SECRET,
    META_APP_SECRET: process.env.META_APP_SECRET,
    NEXT_PUBLIC_DEPLOYMENT_URL: resolveDeploymentUrl(),
    NEXT_PUBLIC_VERCEL_ENV:
      process.env.NEXT_PUBLIC_VERCEL_ENV ?? process.env.VERCEL_ENV,
    CASCADE_DEMO: process.env.CASCADE_DEMO,
    SLACK_SIGNING_SECRET: process.env.SLACK_SIGNING_SECRET,
    SLACK_BOT_TOKEN: process.env.SLACK_BOT_TOKEN,
    DAILY_DOMAIN: process.env.DAILY_DOMAIN,
    DAILY_API_KEY: process.env.DAILY_API_KEY,
    DAILY_WEBHOOK_SECRET: process.env.DAILY_WEBHOOK_SECRET,
    DAILY_API_TIMEOUT_MS: process.env.DAILY_API_TIMEOUT_MS,
    DAILY_PRESENCE_ATTEMPT_TIMEOUT_MS:
      process.env.DAILY_PRESENCE_ATTEMPT_TIMEOUT_MS,
    POSTHOG_API_KEY: process.env.POSTHOG_API_KEY,
    STRIPE_SECRET_KEY: process.env.STRIPE_SECRET_KEY,
    NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY:
      process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY,
    STRIPE_WEBHOOK_SECRET: process.env.STRIPE_WEBHOOK_SECRET,
    CHAT_ENCRYPTION_KEY: process.env.CHAT_ENCRYPTION_KEY,
    NEXT_PUBLIC_PAYPAL_API_KEY: process.env.NEXT_PUBLIC_PAYPAL_API_KEY,
    PAYPAL_API_SECRET: process.env.PAYPAL_API_SECRET,
    OPENAI_API_KEY: process.env.OPENAI_API_KEY,
    AI_GATEWAY_API_KEY: process.env.AI_GATEWAY_API_KEY,
    FB_CONVERSATION_API_KEY: process.env.FB_CONVERSATION_API_KEY,
    BLOB_STORE_ID: process.env.BLOB_STORE_ID,
    BLOB_WEBHOOK_PUBLIC_KEY: process.env.BLOB_WEBHOOK_PUBLIC_KEY,
    VERCEL_ENV: process.env.VERCEL_ENV,
    WHATSAPP_BUSINESS_API_TOKEN: process.env.WHATSAPP_BUSINESS_API_TOKEN,
    FLAGS: process.env.FLAGS,
    FLAGS_SECRET: process.env.FLAGS_SECRET,
    PIXABAY_API_KEY: process.env.PIXABAY_API_KEY,
    DATAFORSEO_PASSWORD: process.env.DATAFORSEO_PASSWORD,
    GOOGLE_SERVICE_ACCOUNT_KEY_BASE64:
      process.env.GOOGLE_SERVICE_ACCOUNT_KEY_BASE64,
    GA4_API_SECRET: process.env.GA4_API_SECRET,
    CRON_SECRET: process.env.CRON_SECRET,
    GOOGLE_ADS_ANALYTICS_SERVICE_ACCOUNT_KEY_BASE64:
      process.env.GOOGLE_ADS_ANALYTICS_SERVICE_ACCOUNT_KEY_BASE64,
    GOOGLE_ADS_DEVELOPER_TOKEN: process.env.GOOGLE_ADS_DEVELOPER_TOKEN,
  },
  /**
   * Run `build` or `dev` with `SKIP_ENV_VALIDATION` to skip env validation. This is especially
   * useful for Docker builds.
   */
  skipValidation: !!process.env.SKIP_ENV_VALIDATION,
  /**
   * Makes it so that empty strings are treated as undefined. `SOME_VAR: z.string()` and
   * `SOME_VAR=''` will throw an error.
   */
  emptyStringAsUndefined: true,
});
